import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Search, Plus, MoreHorizontal } from 'lucide-react';
import { ORG_BASE } from '@/pages/services/organizations/organizations-service';
import { Container, PageHeader, Toolbar } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import {
  Button,
  Input,
  Badge,
  Skeleton,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  IconButton,
} from '@vnetwork/ui/primitives';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { useMyTeams, useDeleteTeam, type Team } from '@/api/teams';
import { CreateTeamDialog } from './components/CreateTeamDialog';
import { TEAM_ROLE_TONE, TEAM_ROLE_LABEL, formatDate } from './shared';

export function TeamList() {
  const navigate = useNavigate();
  const { show } = useToast();
  const { data: teams = [], isLoading } = useMyTeams();

  const [query, setQuery] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [deleting, setDeleting] = useState<Team | null>(null);

  const remove = useDeleteTeam({
    onSuccess: () => {
      show({ tone: 'success', title: 'Team deleted' });
      setDeleting(null);
    },
    onError: () => show({ tone: 'danger', title: 'Could not delete team' }),
  });

  const data = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? teams.filter((t) => t.team_name.toLowerCase().includes(q)) : teams;
  }, [teams, query]);

  const columns = useMemo<ColumnDef<Team, unknown>[]>(
    () => [
      {
        id: 'name',
        accessorKey: 'team_name',
        header: 'Team',
        size: 260,
        cell: ({ row }) => (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)', fontWeight: 500 }}>
            <Users size={14} color="var(--vn-fg-muted)" />
            {row.original.team_name}
          </span>
        ),
      },
      {
        id: 'role',
        header: 'Your role',
        enableSorting: false,
        cell: ({ row }) => {
          const role = row.original.user_role;
          if (!role) return <span style={{ color: 'var(--vn-fg-muted)' }}>—</span>;
          return (
            <Badge tone={TEAM_ROLE_TONE[role]} appearance="subtle" size="sm">
              {TEAM_ROLE_LABEL[role]}
            </Badge>
          );
        },
      },
      {
        id: 'members',
        header: 'Members',
        accessorFn: (t) => t.members_count ?? 0,
        cell: ({ getValue }) => (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{getValue() as number}</span>
        ),
      },
      {
        id: 'created',
        header: 'Created',
        accessorKey: 'created_at',
        size: 110,
        cell: ({ row }) => (
          <span style={{ color: 'var(--vn-fg-muted)' }}>{formatDate(row.original.created_at)}</span>
        ),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => {
          const t = row.original;
          const isOwner = t.user_role === 'owner';
          return (
            <div style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <IconButton variant="ghost" size="sm" aria-label="Team actions">
                    <MoreHorizontal size={16} />
                  </IconButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={() => navigate(`${ORG_BASE}/teams/${t.id}`)}>
                    View details
                  </DropdownMenuItem>
                  {isOwner && (
                    <DropdownMenuItem tone="danger" onSelect={() => setDeleting(t)}>
                      Delete
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
      },
    ],
    [navigate],
  );

  return (
    <Container size="xl">
      <PageHeader
        icon={<Users size={22} />}
        title="Teams"
        description="Group people, manage their roles, and share access across projects."
        actions={
          <Button startIcon={<Plus size={15} />} onClick={() => setCreateOpen(true)}>
            Create team
          </Button>
        }
      />

      <Toolbar
        start={
          <Input
            placeholder="Search teams"
            startSlot={<Search />}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ maxWidth: 320 }}
          />
        }
      />

      {isLoading ? (
        <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} style={{ height: 48 }} />
          ))}
        </div>
      ) : teams.length === 0 ? (
        <EmptyState
          icon={<Users size={28} />}
          title="No teams yet"
          description="Create your first team to start inviting people and sharing access."
          actions={
            <Button startIcon={<Plus size={15} />} onClick={() => setCreateOpen(true)}>
              Create team
            </Button>
          }
        />
      ) : (
        <DataTable
          columns={columns}
          data={data}
          getRowId={(r) => r.id}
          onRowClick={(r) => navigate(`${ORG_BASE}/teams/${r.id}`)}
          emptyState="No teams match your search."
        />
      )}

      <CreateTeamDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(id) => navigate(`${ORG_BASE}/teams/${id}`)}
      />

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(o) => !o && setDeleting(null)}
        title="Delete team?"
        description={`This permanently deletes “${deleting?.team_name}”. This action cannot be undone.`}
        confirmLabel="Delete"
        destructive
        requireText={deleting?.team_name}
        loading={remove.isPending}
        onConfirm={() => deleting && remove.mutate(deleting.id)}
      />
    </Container>
  );
}
